import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import {
  isPlaceholder,
  readPresentation,
  titlePlaceholder,
} from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

export const lektionFolienmaster: Lesson = {
  id: "folienmaster",
  title: "Der Folienmaster",
  summary: "Einmal ändern statt vierzigmal – wo Logo, Fußzeile und Schriftgrößen hingehören.",
  durationMinutes: 11,
  learningGoals: [
    "Zwischen Folienmaster und Layout unterscheiden",
    "Wiederkehrende Elemente im Master statt auf jeder Folie ablegen",
    "Änderungen an Schriftgrößen zentral vornehmen",
  ],
  steps: [
    {
      id: "master-und-layout",
      title: "Master und Layout",
      explanation: [
        "Unter „Ansicht“ → „Folienmaster“ öffnet sich eine eigene Ansicht. Links oben " +
          "steht eine größere Miniatur – das ist der Folienmaster. Darunter, leicht " +
          "eingerückt, stehen die Layouts, die zu ihm gehören.",
        "Die Regel ist einfach: Was für alle Folien gilt, gehört auf den Master. Was nur " +
          "für eine bestimmte Folienart gilt, gehört ins Layout. Ein Logo in der Ecke " +
          "kommt auf den Master. Ein Bildplatzhalter kommt in das Layout für Bildfolien.",
        "Änderungen am Master schlagen auf alle Layouts durch, sofern das jeweilige " +
          "Layout nicht ausdrücklich etwas anderes festlegt. Das ist derselbe " +
          "Vererbungsgedanke wie bei den Formatvorlagen im Word-Kurs, wo alle Vorlagen " +
          "von „Standard“ erben.",
        "Zum Schließen der Ansicht gibt es auf der Registerkarte „Folienmaster“ die " +
          "Schaltfläche „Masteransicht schließen“. Wer stattdessen einfach auf eine Folie " +
          "klickt, arbeitet unbemerkt weiter im Master – ein häufiger und ärgerlicher " +
          "Fehler.",
      ],
      keyPoints: [
        "Ansicht → Folienmaster; die große Miniatur oben ist der Master.",
        "Für alle Folien → Master. Für eine Folienart → Layout.",
        "Immer über „Masteransicht schließen“ zurückwechseln.",
      ],
      task: {
        instruction:
          "Prüfe, dass alle deine Folien Layouts desselben Folienmasters verwenden.",
        hint: "Ansicht → Folienmaster: Steht dort mehr als eine große Miniatur, verteilen sich die Folien auf mehrere Master.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.shapes) {
            return nichtPruefbar(
              "Der Folienmaster einer Folie",
              "1.3",
              "Öffne „Ansicht“ → „Folienmaster“ und sieh nach, wie viele große Miniaturen links stehen.",
            );
          }
          if (snapshot.slides.length === 0) {
            return fail(
              "In der Präsentation ist noch keine Folie vorhanden.",
              "Lege zuerst Folien an.",
            );
          }

          const namen = new Map<string, number[]>();
          for (const folie of snapshot.slides) {
            const name = folie.masterName ?? "unbekannt";
            namen.set(name, [...(namen.get(name) ?? []), folie.index + 1]);
          }

          if (namen.size <= 1) {
            const [name] = [...namen.keys()];
            return pass(
              `Alle ${snapshot.slides.length} Folien gehören zum Master „${name}“ – eine Änderung dort erreicht jede Folie.`,
            );
          }

          const verteilung = [...namen.entries()]
            .map(([name, folien]) => `„${name}“ (Folie ${folien.join(", ")})`)
            .join("; ");
          return fail(
            `Die Folien verteilen sich auf ${namen.size} Folienmaster: ${verteilung}.`,
            "Das passiert beim Einfügen fremder Folien mit „Ursprüngliche Formatierung beibehalten“. " +
              "Unter „Ansicht“ → „Folienmaster“ den überflüssigen Master per Rechtsklick löschen – die Folien wechseln dann automatisch.",
          );
        },
      },
    },
    {
      id: "wiederholte-objekte",
      title: "Wiederkehrende Elemente gehören in den Master",
      explanation: [
        "Ein Logo, das auf jeder Folie unten rechts steht, wird erstaunlich oft auf jede " +
          "einzelne Folie kopiert. Das funktioniert – bis das Logo sich ändert, bis eine " +
          "Kopie um zwei Millimeter verrutscht oder bis jemand eine Folie einfügt und das " +
          "Logo vergisst.",
        "Im Folienmaster steht es einmal und erscheint überall. Ändert sich das Logo, " +
          "tauscht man eine Datei aus. Neue Folien bekommen es automatisch. Und " +
          "verrutschen kann es nicht, weil es auf der einzelnen Folie gar nicht " +
          "anfassbar ist.",
        "Dasselbe gilt für Fußzeilen, Foliennummern und das Datum. Dafür gibt es sogar " +
          "eigene Platzhalter: „Einfügen“ → „Kopf- und Fußzeile“ blendet sie ein und " +
          "füllt sie. Die Foliennummer ist dabei ein Feld, das automatisch mitzählt – " +
          "genau wie das Seitenzahlenfeld im Word-Kurs und aus demselben Grund besser " +
          "als eine getippte Zahl.",
      ],
      keyPoints: [
        "Einfügen → Kopf- und Fußzeile für Fußzeile, Datum und Foliennummer.",
        "Die Foliennummer ist ein Feld und zählt automatisch mit.",
        "Objekte auf dem Master lassen sich auf der Folie nicht versehentlich verschieben.",
      ],
      task: {
        instruction:
          "Prüfe, ob du ein Element auf mehreren Folien kopiert hast, das eigentlich in " +
          "den Folienmaster gehört – etwa ein Logo an derselben Stelle.",
        hint: "Ansicht → Folienmaster, das Element dort einmal einfügen und die Kopien auf den Folien löschen.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.shapeDetails) {
            return nichtPruefbar(
              "Name, Position und Größe der Formen",
              "1.4",
              "Blättere die Folien durch: Kommt dasselbe Element mehrfach an derselben Stelle vor?",
            );
          }
          if (snapshot.slides.length < 3) {
            return fail(
              "Für diese Prüfung braucht es mindestens drei Folien.",
              "Lege zuerst weitere Folien an – erst dann lässt sich eine Wiederholung erkennen.",
            );
          }

          // Ein Element gilt als kopiert, wenn eine Form gleichen Typs an
          // praktisch derselben Stelle auf mehreren Folien liegt.
          const raster = new Map<string, Set<number>>();
          for (const folie of snapshot.slides) {
            for (const form of folie.shapes) {
              if (isPlaceholder(form)) continue;
              if (form.left === null || form.top === null) continue;
              const schluessel = `${form.type}@${Math.round(form.left / 4)}:${Math.round(form.top / 4)}`;
              const folien = raster.get(schluessel) ?? new Set<number>();
              folien.add(folie.index);
              raster.set(schluessel, folien);
            }
          }

          const schwelle = Math.max(3, Math.ceil(snapshot.slides.length * 0.6));
          const wiederholt = [...raster.entries()].filter(
            ([, folien]) => folien.size >= schwelle,
          );

          if (wiederholt.length === 0) {
            return pass(
              "Kein Objekt liegt auf mehreren Folien an derselben Stelle – wiederkehrende Elemente sind also nicht kopiert.",
            );
          }

          const [schluessel, folien] = wiederholt[0];
          const typ = schluessel.split("@")[0];
          return fail(
            `Ein Objekt vom Typ „${typ}“ liegt auf ${folien.size} von ${snapshot.slides.length} Folien an derselben Stelle.`,
            "Solche Elemente gehören in den Folienmaster: Ansicht → Folienmaster, dort einmal einfügen, " +
              "Masteransicht schließen und die Kopien auf den Folien löschen. Ist die Wiederholung gewollt, " +
              "kannst du den Schritt so lassen und selbst abhaken.",
          );
        },
      },
    },
    {
      id: "schriftgroessen-zentral",
      title: "Schriftgrößen zentral ändern",
      explanation: [
        "Der häufigste sinnvolle Eingriff im Folienmaster betrifft die Schriftgrößen. " +
          "Die Voreinstellungen von PowerPoint sind großzügig bemessen – 44 pt für den " +
          "Titel –, was für kurze Titel gut ist und bei längeren zum automatischen " +
          "Verkleinern führt.",
        "Statt das auf jeder Folie einzeln zu korrigieren, wird im Master die " +
          "Titelgröße einmal auf etwa 32 pt gesetzt. Danach passen auch längere Titel, " +
          "ohne dass PowerPoint eingreift, und alle Folien haben dieselbe Titelgröße.",
        "Dasselbe gilt für die Gliederungsebenen im Textbereich: Im Master steht für " +
          "jede Ebene eine eigene Größe. Wer sie dort anpasst, ändert sie in der ganzen " +
          "Präsentation – und die Abstufung zwischen den Ebenen bleibt erhalten.",
        "Eine Warnung zum Schluss: Änderungen im Master wirken nur auf Folien, deren " +
          "Platzhalter nicht von Hand umformatiert wurden. Wer vorher auf einzelnen " +
          "Folien Schriftgrößen gesetzt hat, muss diese Folien anschließend über „Start“ " +
          "→ „Zurücksetzen“ wieder an das Layout binden.",
      ],
      keyPoints: [
        "Titelgröße im Master statt auf jeder Folie ändern.",
        "Jede Gliederungsebene hat im Master ihre eigene Größe.",
        "Nach Masteränderungen die Folien mit „Zurücksetzen“ nachziehen.",
      ],
      task: {
        instruction:
          "Sorge dafür, dass alle Folientitel dieselbe Schriftgröße haben – sie soll aus " +
          "dem Layout kommen und nicht Folie für Folie gesetzt sein.",
        hint: "Wenn eine Größe abweicht: Folie markieren und „Start“ → „Zurücksetzen“ drücken.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.text) {
            return nichtPruefbar(
              "Die Schriftgröße im Textrahmen",
              "1.4",
              "Klick nacheinander in die Titel und vergleiche die Schriftgrößenanzeige.",
            );
          }

          const titel = snapshot.slides
            .map((folie) => ({ nummer: folie.index + 1, form: titlePlaceholder(folie) }))
            .filter(
              (eintrag): eintrag is { nummer: number; form: NonNullable<typeof eintrag.form> } =>
                eintrag.form !== null && eintrag.form.paragraphs.length > 0,
            );

          if (titel.length < 2) {
            return fail(
              "Es gibt noch weniger als zwei gefüllte Folientitel.",
              "Gib zuerst mehreren Folien einen Titel.",
            );
          }

          const groessen = new Map<number, number[]>();
          for (const eintrag of titel) {
            const groesse = eintrag.form.font?.size;
            if (typeof groesse !== "number") continue;
            groessen.set(groesse, [...(groessen.get(groesse) ?? []), eintrag.nummer]);
          }

          if (groessen.size === 0) {
            return fail(
              "Für keinen Titel ließ sich eine eindeutige Schriftgröße auslesen – die Titel sind gemischt formatiert.",
              "Markiere die betroffenen Folien und drück „Start“ → „Zurücksetzen“. Danach gilt wieder die Größe aus dem Layout.",
            );
          }
          if (groessen.size === 1) {
            const [[groesse]] = [...groessen.entries()];
            return pass(
              `Alle ${titel.length} Titel stehen einheitlich bei ${groesse} pt.`,
            );
          }

          const verteilung = [...groessen.entries()]
            .sort((a, b) => b[1].length - a[1].length)
            .map(([groesse, folien]) => `${groesse} pt auf Folie ${folien.join(", ")}`)
            .join("; ");
          return fail(
            `Die Titel haben ${groessen.size} verschiedene Schriftgrößen: ${verteilung}.`,
            "Die abweichenden Folien markieren und „Start“ → „Zurücksetzen“ drücken. Soll die Größe generell anders sein, ändere sie im Folienmaster.",
          );
        },
      },
    },
  ],
};
